// Page thumbnails under the document on screen in the package rail
// (Documents tab PR 9b, mockup v3 screen 6, `.thumbs`). Drawn from the
// pages the screen has already rendered, so nothing is read or fetched
// again; a click scrolls to that page.

import { useMemo } from "react";

import type { RenderedPdfPage } from "./render-pdf-pages";
import { pageNeedsText } from "./toolbar-helpers";

/** Thumbnail height in CSS pixels; the drawing's 44 x 56. */
const THUMB_HEIGHT = 56;

/** A page's picture as a small data URL, or null when the browser can't
 *  draw it (a test DOM, a tainted canvas): the button then shows the
 *  page number alone. */
function thumbnailOf(page: RenderedPdfPage): string | null {
  try {
    const ratio = page.widthPt > 0 && page.heightPt > 0 ? page.widthPt / page.heightPt : 0.77;
    const small = document.createElement("canvas");
    small.height = THUMB_HEIGHT * 2;
    small.width = Math.max(1, Math.round(small.height * ratio));
    const ctx = small.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(page.canvas, 0, 0, small.width, small.height);
    return small.toDataURL("image/png");
  } catch {
    return null;
  }
}

export interface PageThumbsProps {
  pages: readonly RenderedPdfPage[];
  /** The page last jumped to, outlined. */
  current: number;
  /** Blanks still the sender's to answer, by page number. A page with none
   *  gets a green dot, one with some an amber dot. Omitted draws no dots. */
  needs?: ReadonlyMap<number, number> | undefined;
  onJump: (pageNumber: number) => void;
}

export function PageThumbs({ pages, current, needs, onJump }: PageThumbsProps) {
  const pictures = useMemo(() => pages.map((p) => ({ page: p.pageNumber, src: thumbnailOf(p) })), [pages]);
  if (pages.length < 2) return null;
  return (
    <div className="esign-prep-thumbs" role="group" aria-label="Pages">
      {pictures.map(({ page, src }) => (
        <button
          key={page}
          type="button"
          className="esign-prep-thumb"
          data-on={page === current ? "true" : undefined}
          aria-label={`Go to page ${page}${needs ? `, ${pageNeedsText(needs.get(page) ?? 0).toLowerCase()}` : ""}`}
          aria-current={page === current ? "true" : undefined}
          onClick={() => onJump(page)}
        >
          {src ? <img src={src} alt="" /> : <span>{page}</span>}
          {needs ? (
            <i
              className="esign-prep-thumb-dot"
              data-need={(needs.get(page) ?? 0) > 0 ? "true" : undefined}
              aria-hidden="true"
            />
          ) : null}
        </button>
      ))}
    </div>
  );
}
